import { useEffect, useState } from "react";
import { LifeBuoy, Plus, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function Support() {
    const [tickets, setTickets] = useState(null);
    const [filter, setFilter] = useState("active");
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [newOpen, setNewOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/support?status=${filter}`).then((r) => setTickets(r.data)).catch(() => setTickets([]));
    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const openTicket = async (t) => {
        const r = await api.get(`/support/${t.ticket_id}`);
        setActive(r.data);
    };

    const create = async () => {
        if (!subject.trim() || !message.trim()) return toast.error("Konu ve mesaj zorunludur");
        setBusy(true);
        try {
            const r = await api.post("/support", { subject: subject.trim(), message: message.trim() });
            toast.success(`Talebiniz alındı — takip kodu: ${r.data.code}`);
            setNewOpen(false); setSubject(""); setMessage("");
            setFilter("active");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        } finally {
            setBusy(false);
        }
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/support/${active.ticket_id}/messages`, { content: reply.trim() });
            setReply("");
            openTicket(active);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gönderilemedi");
        } finally {
            setBusy(false);
        }
    };

    const close = async (t) => {
        await api.post(`/support/${t.ticket_id}/close`);
        toast.success(`${t.code} kapatıldı`);
        setActive(null);
        load();
    };

    const reopen = async (t) => {
        await api.post(`/support/${t.ticket_id}/reopen`);
        toast.success(`${t.code} yeniden açıldı — mesaj ekleyebilirsiniz`);
        setActive(null);
        load();
    };

    return (
        <div className="space-y-6" data-testid="support-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Destek</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Platform kullanımıyla ilgili talepleriniz — her talebe benzersiz bir takip kodu verilir.</p>
                </div>
                <Button onClick={() => setNewOpen(true)} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="support-new-button">
                    <Plus className="mr-2 h-4 w-4" /> Yeni Talep
                </Button>
            </div>

            <div className="flex gap-2">
                {[["active", "Aktif"], ["passive", "Pasif"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-1.5 text-xs font-medium ${filter === k ? "border-primary bg-primary text-primary-foreground" : ""}`} data-testid={`support-filter-${k}`}>{l}</button>
                ))}
            </div>

            {!tickets ? (
                <div className="h-48 animate-pulse rounded-2xl bg-muted" />
            ) : (
                <div className="space-y-3">
                    {tickets.map((t) => (
                        <button key={t.ticket_id} onClick={() => openTicket(t)} className="w-full rounded-2xl border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md" data-testid={`ticket-${t.ticket_id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <LifeBuoy className="h-5 w-5 text-terra-600" />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-bold text-primary">{t.code}</span>
                                            <p className="font-semibold">{t.subject}</p>
                                        </div>
                                        <p className="mt-0.5 text-xs text-muted-foreground">{t.messages.length} mesaj • {fmtDate(t.created_at)}</p>
                                    </div>
                                </div>
                                <Badge variant="outline" className={`text-[10px] ${t.status === "active" ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-stone-200 bg-stone-100 text-stone-600"}`}>
                                    {t.status === "active" ? "Aktif" : "Kapalı"}
                                </Badge>
                            </div>
                        </button>
                    ))}
                    {!tickets.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground" data-testid="tickets-empty">Talep yok.</p>}
                </div>
            )}

            <Dialog open={!!active} onOpenChange={() => setActive(null)}>
                <DialogContent className="max-w-lg" data-testid="ticket-dialog">
                    {active && (
                        <>
                            <DialogHeader><DialogTitle className="font-display"><span className="font-mono text-sm text-primary">{active.code}</span> — {active.subject}</DialogTitle></DialogHeader>
                            <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg bg-muted/40 p-3">
                                {active.messages.map((m, i) => (
                                    <div key={i} className={`rounded-xl px-3 py-2 text-sm ${m.role === "admin" ? "ml-8 bg-terra-600/10" : "mr-8 border bg-card"}`}>
                                        <p className="text-[10px] font-bold uppercase text-muted-foreground">{m.sender} • {fmtDate(m.at)}</p>
                                        <p className="whitespace-pre-wrap">{m.content}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Mesaj ekleyin..." data-testid="ticket-reply-input" />
                                <Button onClick={sendReply} disabled={busy || !reply.trim()} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="ticket-reply-send"><Send className="h-4 w-4" /></Button>
                            </div>
                            <div className="flex justify-end">
                                {active.status === "active" ? (
                                    <Button size="sm" variant="outline" onClick={() => close(active)} data-testid="ticket-close">Talebi Kapat</Button>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={() => reopen(active)} data-testid="ticket-reopen"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Yeniden Aç</Button>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogContent data-testid="new-ticket-dialog">
                    <DialogHeader><DialogTitle className="font-display">Yeni Destek Talebi</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Konu *" data-testid="ticket-subject" />
                        <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Sorununuzu açıklayın *" data-testid="ticket-message" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewOpen(false)}>Vazgeç</Button>
                        <Button onClick={create} disabled={busy} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="ticket-submit">{busy ? "Gönderiliyor..." : "Gönder"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
